import { sanitizeCmsHtml, sanitizeIframeEmbed } from '@shared/helpers/sanitize-cms-html';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Renderer2,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ComponentDependencyJunctionService } from '@app/core/services/component-dependency-junction.service';
import { EditorService } from '@app/modules/editor/services/editor.service';
import { LocalStorageService, SessionStorageService } from '@app/shared/services/storage';

import { BlockActionType, BlockNodeEnum, BlockTypeEnum, FORM_FIELD_NODES } from '@core/enums';
import { MixedObject } from '@core/interfaces/mixed-object';
import { Block } from '@core/models/block/block.model';
import { GlobalService, MediaQuery } from '@core/services/common/global.service';
import { PageService } from '@core/services/page/page.service';
import { SubSink } from '@core/utils/subsink';
import { blockStyleMinWidth, blockStyleWidth } from '@modules/creator/components/block/block-appearance-functions';
import { IframeComponent } from '@modules/creator/components/iframe/iframe.component';
import { mergeAllStyles } from '@shared/helpers/block-helpers';
import { anyParentHasAriaLabel, anyParentHasTabIndex } from '@shared/helpers/dom-helpers';
import { isVideo } from '@shared/helpers/media-helpers';
import { EditionDimensionsService } from '@shared/services/edition/edition-dimensions.service';
import { ListDetailsService } from '@shared/services/list/list-details.service';
import { SubmenuService } from '@shared/services/submenu.service';
import { UnicornStudioService } from '@shared/services/unicorn-studio.service';
import { camelCase } from 'lodash';
import { MediaComponent } from '../media/media.component';

const filterTypes = [
  BlockTypeEnum.FILTER_SEARCH_INPUT_TYPE,
  BlockTypeEnum.FILTER_GROUP_SELECTOR_TYPE,
  BlockTypeEnum.FILTER_SINGLE_SELECTOR_TYPE,
  BlockTypeEnum.SLIDER_PRICE_RANGE_TYPE,
  BlockTypeEnum.SLIDER_YEAR_OF_CREATION,
  BlockTypeEnum.ADVANCED_FILTER_GROUP_SELECTOR_TYPE,
  BlockTypeEnum.RANGE_YEAR_OF_CREATION,
  BlockTypeEnum.RANGE_PRICE_OF_CREATION,
];

@Component({
  selector: '[app-block]',
  templateUrl: './block.component.html',
  styleUrls: ['./block.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [SubmenuService],
  standalone: false,
})
export class BlockComponent implements OnInit, AfterViewInit, OnDestroy {
  blockNodeEnum = BlockNodeEnum;
  isHovered = false;
  iframeContent: SafeHtml = '';
  tabindexAddedToParent = false;
  blockHasTabindex = false;
  @Input() disableTabIndex = false;

  public item: Block;
  extraData: any = {};
  @ViewChild('mediaTemplate', {read: ViewContainerRef})
  mediaTemplateRef: ViewContainerRef;
  @ViewChild('iframeTemplate', {read: ViewContainerRef})
  iframeTemplateRef: ViewContainerRef;
  blockStyles: MixedObject = {};
  private subSink = new SubSink();
  public itemContent: SafeHtml = '';

  constructor(
    private renderer: Renderer2,
    private element: ElementRef,
    private globalService: GlobalService,
    private sanitizer: DomSanitizer,
    public localStorageService: LocalStorageService,
    private sessionStorageService: SessionStorageService,
    private pageService: PageService,
    public editorService: EditorService,
    private unicornStudioService: UnicornStudioService,
    @Optional() private junction: ComponentDependencyJunctionService,
  ) {
  }

  @Input('item') set _item(value: Block) {
    this.item = value;
    if (this.item?.type === BlockTypeEnum.FILTER_GROUP_SELECTOR_TYPE) {
      this.extraData.filterGroupSelectorStyles = this.item.styles;
    }
    // Odśwież blockStyles w następnym cyklu (unika NG0100), żeby text_collapse_max_length było dostępne na stronie podglądu pracy
    if (this.item) {
      const mq = this.globalService?.mediaQuery?.value;
      if (mq) {
        this.generateBlockStyles(mq);
      }
      this.updateBlockContent();
    }
  }

  get isSpecialBlock(): boolean {
    return (
      (this.item.specialBlockId !== undefined &&
        this.item.specialBlockId !== null &&
        !!this.item.specialBlock) ||
      filterTypes.includes(this.item.type)
    );

    //    this.type !== BlockTypeEnum.UNIVERSAL_TYPE;
  }

  get wrappedByContainer() {
    return (
      this.item.specialBlockId !== undefined &&
      this.item.specialBlockId !== null &&
      !!this.item.specialBlock
    );
  }

  get unicornAnimationDivId() {
    // item może już nie istnieć w trakcie niszczenia widoku (ngOnDestroy)
    return (this.item?.uId ?? '') + '-unicorn-animation';
  }

  get canAddTabIndexToChild(): boolean {
    return !this.disableTabIndex
      && !this.hasAnchorInContent
      && !this.tabindexAddedToParent
      && !this.blockHasTabindex;
  }

  get hasAnchorInContent(): boolean {
    const content = this.item.content ?? '';
    const doc = new DOMParser().parseFromString(content, 'text/html');
    return !!doc.querySelector('a');
  }

  ngOnInit(): void {
    this.subSink.sink = this.globalService.mediaQuery.subscribe((mediaQuery) => {
      this.generateBlockStyles(mediaQuery);
      this.setComponentStyles();
      this.unicornStudioService.resizeScene(this.unicornAnimationDivId);
    });
    this.generateBlockStyles(this.globalService.mediaQuery.value);
    this.setIframeContentHtml();
    this.setComponentStyles();
    this.initAnimationUnicorn();
    this.updateBlockContent();
  }

  private updateBlockContent() {
    const content = `${this.item.content}`;
    const maLicznik = content.includes('data-snippet-name="items_count_on_list"');
    // Snippet „Wymiary" wybranej edycji — reaktywny tak samo jak licznik pozycji: konfigurator
    // ceny publikuje wartość, a ten blok podmienia ją w treści przy każdej zmianie edycji.
    const maWymiary = content.includes('data-snippet-name="edition_dimensions"');

    if (!maLicznik && !maWymiary) {
      this.itemContent = content;
      return;
    }

    // Każde odświeżenie składa treść od nowa z oryginału, więc wiele reaktywnych snippetów
    // (licznik i wymiary naraz) nie kasuje się nawzajem, a wartość zawsze jest bieżąca.
    const render = () => {
      const div = document.createElement('div');
      // AT-9147: nawet odpięty od DOM element wykonuje `onerror`, więc treść czyścimy przed `innerHTML`.
      div.innerHTML = sanitizeCmsHtml(content);

      if (maLicznik) {
        const count = ListDetailsService.objectCountOnList.value;
        div.querySelectorAll('.ql-snippet[data-snippet-name="items_count_on_list"]')
          .forEach(snippet => snippet.replaceWith(document.createTextNode(`${count}`)));
      }

      if (maWymiary) {
        const wartosc = EditionDimensionsService.value.value;
        div.querySelectorAll('.ql-snippet[data-snippet-name="edition_dimensions"]')
          .forEach(snippet => {
            // Wartość może zawierać `<br>` (kilka wymiarów), więc wstawiamy sparsowane węzły,
            // nie surowy tekst — inaczej znaczniki pokazałyby się dosłownie.
            const kontener = document.createElement('div');
            kontener.innerHTML = sanitizeCmsHtml(wartosc);
            snippet.replaceWith(...Array.from(kontener.childNodes));
          });
      }

      this.itemContent = div.innerHTML;
    };

    render();

    if (maLicznik) {
      this.subSink.sink = ListDetailsService.objectCountOnList.subscribe(() => render());
    }

    if (maWymiary) {
      this.subSink.sink = EditionDimensionsService.value.subscribe(() => render());
    }
  }

  /** True, gdy w stylach bloku jest text_collapse_max_length – wtedy każdy blok (paragraf, snippet itd.) dostaje zwijanie. */
  shouldUseTextCollapse(): boolean {
    return this.getTextCollapseMaxLength() > 0;
  }

  getTextCollapseMaxLength(): number {
    // Sprawdź w blockStyles.basic (snake_case i camelCase)
    if (this.blockStyles?.basic?.text_collapse_max_length) {
      return Number(this.blockStyles.basic.text_collapse_max_length);
    }
    if (this.blockStyles?.basic?.textCollapseMaxLength) {
      return Number(this.blockStyles.basic.textCollapseMaxLength);
    }

    // Sprawdź bezpośrednio w item.styles.all.basic (snake_case i camelCase)
    if (this.item.styles?.all?.basic?.text_collapse_max_length) {
      return Number(this.item.styles.all.basic.text_collapse_max_length);
    }
    if (this.item.styles?.all?.basic?.textCollapseMaxLength) {
      return Number(this.item.styles.all.basic.textCollapseMaxLength);
    }

    // Sprawdź w innych media queries (snake_case i camelCase)
    if (this.item.styles) {
      for (const key of Object.keys(this.item.styles)) {
        if (this.item.styles[key]?.basic?.text_collapse_max_length) {
          return Number(this.item.styles[key].basic.text_collapse_max_length);
        }
        if (this.item.styles[key]?.basic?.textCollapseMaxLength) {
          return Number(this.item.styles[key].basic.textCollapseMaxLength);
        }
      }
    }

    return 0;
  }

  initAnimationUnicorn() {
    const lang = this.sessionStorageService.getItem('language');
    if (!lang) {
      return;
    }
    if (!this.item.backgroundAnimation?.[lang]) {
      return;
    }
    this.unicornStudioService.loadScript()
      .then(() => {
        this.unicornStudioService.runAnimation(this.unicornAnimationDivId, this.item.backgroundAnimation[lang]);
      });
  }

  parseTabindex() {
    return this.canAddTabIndexToChild
      ? this.item.tabindex
      : null;
  }

  ngAfterViewInit(): void {
    if (this.hasMedia()) {
      this.renderMediaElement();
      this.renderIframeElement();
    }
    if (
      (this.item.isVideoSnippet ||
        this.item.node === BlockNodeEnum.VIDEO_NODE) &&
      !this.item.media
    ) {
      this.element.nativeElement.style.height = 'auto';
    }
    if (this.item.tabindex > -1) {
      this.tabindexAddedToParent = anyParentHasTabIndex(
        this.element.nativeElement,
      );
    }
    this.generateBlockStyles(this.globalService.mediaQuery.value);
    this.setComponentStyles();
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
    this.unicornStudioService.destroyScene(this.unicornAnimationDivId);
  }

  getIconName(): string {
    if (this.element.nativeElement.closest('app-filter-search-input')) {
      return this.junction?.textSearchActive ? 'clear' : this.item.content;
    }
    return this.item.content;
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const isInsideMultiSelect =
      target.closest('.filter-group-selector') !== null;
    const isInsideMultiItem = target.closest('.filter-select-item') !== null;
    if (!isInsideMultiSelect && !isInsideMultiItem) {
      this.localStorageService.closeModalFilter = true;
    } else if (this.localStorageService.closeModalFilter) {
      this.localStorageService.closeModalFilter = false;
    }
  }

  private generateBlockStyles(mediaQuery: MediaQuery): void {
    // Klucze w item.styles z API są w snake_case (all, max_width_991_px, max_width_479_px)
    const queryKey = mediaQuery?.query ?? 'all';
    this.blockStyles = mergeAllStyles(this.item.styles, camelCase(queryKey));
  }

  private setComponentStyles(): void {
    if (!this.item?.styles) {
      return;
    }
    const item = this.item;
    let minWidth = blockStyleMinWidth(this.blockStyles);
    if (minWidth === '100dvw') {
      minWidth = '100%';
    }
    this.setStyle('minWidth', minWidth);
    const width = blockStyleWidth(item, this.blockStyles);
    let itemWidth = width;
    if (item?.link?.hasExternalLink() && width.includes('%')) {
      itemWidth = '100%';
    }
    this.setStyle('width', itemWidth);
    const boxSizing = item.node !== BlockNodeEnum.SVG_NODE ? '' : 'content-box';
    this.setStyle('boxSizing', boxSizing);
    const styles = this.blockStyles.basic;
    const textGradient =
      styles?.backgroundClip === 'text' ||
      styles?.['-webkitBackgroundClip'] === 'text';
    if (textGradient) {
      this.addClass('textGradient');
    } else {
      this.removeClass('textGradient');
    }

    if (item.useAsButton) {
      // dodajemy klasę 'button' na początku.
      if (!this.element.nativeElement.classList.contains('button')) {
        const className = this.element.nativeElement.className;
        this.element.nativeElement.className = 'button ' + className;
      }
    } else {
      this.removeClass('button');
    }

    if (item.modelId) {
      this.addClass(`m${item.modelId}`);
    }

    if (item.node === BlockNodeEnum.HORIZONTAL_LINE) {
      this.addClass('horizontalLine');
    } else {
      this.removeClass('horizontalLine');
    }
    if (!!item.image && !width) {
      this.addClass('full-width');
    } else {
      this.removeClass('full-width');
    }
    if (this.blockStyles.hover.width) {
      this.addClass('change-width-on-hover');
    } else {
      this.removeClass('change-width-on-hover');
    }
    if (item.additionalClasses?.length) {
      item.additionalClasses.forEach((cls) => this.addClass(cls));
    }
    if (item.modelId) {
      this.addClass(`m${item.modelId}`);
    }
    const parallaxSpeed = this.getParallaxSpeed();
    if (parallaxSpeed !== 0) {
      this.setAttribute('data-parallax-speed', parallaxSpeed);
    }
    this.setAttribute('data-b-id', item.uId);
    this.setAttribute('data-c-id', item.uId);
    if (item.anchor) {
      this.setAttribute('id', item.anchor);
    } else {
      this.removeAttribute('id');
    }
    const animationName = this.blockStyles?.basic?.animationName
      || this.blockStyles?.basic?.['--animation-name'];
    if (animationName) {
      this.setAttribute('animation-name', animationName);
    } else {
      this.removeAttribute('animation-name');
    }
    const transitionDuration = this.blockStyles?.basic?.['--transition-duration'];
    if (transitionDuration) {
      this.setAttribute('transition-duration', transitionDuration);
    } else {
      this.removeAttribute('transition-duration');
    }
    if (item.settings?.viewPart) {
      this.setAttribute('view-part', item.settings?.viewPart);
    } else {
      this.removeAttribute('view-part');
    }
    // AT-9068 fly-in: eksponuj snippetName w DOM jako marker źródła animacji (analogicznie do view-part).
    // snippet_name z backendu = pełny string snippetu (np. `<<func(inventoryGetMainPhoto)>>`).
    if (item.snippetName) {
      this.setAttribute('snippet-name', item.snippetName);
    } else {
      this.removeAttribute('snippet-name');
    }
    if (item?.action?.actionType) {
      this.setAttribute('role', 'button');
      this.setAttribute(
        'action-name',
        BlockActionType[item?.action?.actionType],
      );
      this.setAttribute(
        'action-data',
        item?.action?.actionData ? JSON.stringify(item?.action?.actionData) : '',
      );

      if (item?.action.actionType === BlockActionType.PREVIOUS_OBJECT) {
        this.element.nativeElement.addEventListener('mouseover', () => {
          this.setNeighbourTooltip(this.pageService.neighbourModels.value?.previousModel?.title);
        });
      }
      if (item?.action.actionType === BlockActionType.NEXT_OBJECT) {
        this.element.nativeElement.addEventListener('mouseover', () => {
          this.setNeighbourTooltip(this.pageService.neighbourModels.value?.nextModel?.title);
        });
      }
    }
    if (item?.linkDescription) {
      if (!anyParentHasAriaLabel(this.element.nativeElement, item.linkDescription)) {
        this.setAttribute('aria-label', item.linkDescription);
      }
    } else {
      this.removeAttribute('aria-label');
    }
    if (item?.node === BlockNodeEnum.VIDEO_NODE) {
      this.addClass('video-block');
    } else {
      this.removeClass('video-block');
    }

    const parentHasTabIndex = anyParentHasTabIndex(this.element.nativeElement);
    const linkCondition = !item.link?.hasLink() || this.element.nativeElement.tagName === 'A';
    const validType = item.type !== BlockTypeEnum.FILTER_SINGLE_SELECTOR_TYPE;
    if (
      item.tabindex >= 0
      && !FORM_FIELD_NODES.includes(item.node)
      && !parentHasTabIndex
      && linkCondition
      && !this.hasAnchorInContent
      && !this.disableTabIndex
      && validType
    ) {
      this.setAttribute('tabindex', item.tabindex);
      this.blockHasTabindex = true;
    } else {
      this.removeAttribute('tabindex');
      this.blockHasTabindex = false;
    }
  }

  private setAttribute(name: string, value: any) {
    this.renderer.setAttribute(this.element.nativeElement, name, value);
  }

  /**
   * AT-8900: brak sąsiada (koniec listy sąsiadów albo zgubiony kontekst listy) nie
   * może pokazywać w tooltipie literalnego stringa „undefined” — natywny setAttribute
   * konwertuje na string każdą wartość, więc brak tytułu usuwa atrybut zamiast go ustawiać.
   */
  private setNeighbourTooltip(title: string | null | undefined) {
    if (title) {
      this.setAttribute('title', title);
    } else {
      this.removeAttribute('title');
    }
  }

  private removeAttribute(name: string) {
    this.renderer.removeAttribute(this.element.nativeElement, name);
  }

  private setStyle(property: string, value: any) {
    this.renderer.setStyle(this.element.nativeElement, property, value);
  }

  private addClass(value: string) {
    this.renderer.addClass(this.element.nativeElement, value);
  }

  private removeClass(value: string) {
    this.renderer.removeClass(this.element.nativeElement, value);
  }

  private getParallaxSpeed(): number {
    const styles = this.blockStyles.basic;

    return +(styles?.['--paralaxSpeed'] || 0);
  }

  private hasMedia(): boolean {
    return !!this.item.media;
  }

  private renderMediaElement(): void {
    if (isVideo(this.item.media) && !this.item.image) {
      const mediaComponent = this.mediaTemplateRef.createComponent(MediaComponent);
      mediaComponent.instance.media = this.item.media;
      mediaComponent.instance.isVideoBlock = this.item.node === BlockNodeEnum.VIDEO_NODE;

      if (!this.blockStyles.basic.position) {
        this.element.nativeElement.style.position = 'relative';
      }
      this.element.nativeElement.style.objectFit = 'cover';
    }
  }

  private renderIframeElement(): void {
    if (this.item.media.type === 'iframe') {
      const iframeComponent = this.iframeTemplateRef.createComponent(IframeComponent);
      iframeComponent.instance.media = this.item.media;
    }
  }

  private setIframeContentHtml(): void {
    const {content = '', node} = this.item;
    if (node !== BlockNodeEnum.IFRAME_NODE || !content || this.iframeContent) {
      return;
    }
    const doc = new DOMParser().parseFromString(content, 'text/html');
    const iframe = doc.querySelector('iframe');

    if (!iframe) {
      return;
    }

    const {
      basic: {width, height, minHeight, aspectRatio},
    } = this.blockStyles;
    const _height = minHeight ?? height;

    iframe.setAttribute('width', width.includes('%') ? '100%' : width || '');
    iframe.setAttribute('height', _height || '');
    if (aspectRatio) {
      iframe.style.aspectRatio = aspectRatio;
    }

    // AT-9147: iframe z treści (osadzenie to funkcja produktu) wpuszczamy tylko z `https:`
    // i bez `srcdoc`/atrybutów zdarzeń; reszta przechodzi przez DOMPurify.
    this.iframeContent = this.sanitizer.bypassSecurityTrustHtml(
      sanitizeIframeEmbed(iframe.outerHTML),
    );
  }
}
